import { Component, computed, effect, HostListener, inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition
} from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import {
  DeviceCompatibility,
  Distribution,
  FileUpload,
  FrontendFirmwarePackageDetails,
  ManifestInformation,
  mapToPackageUploadMetadata,
  MultiStepForm,
  openConfirmDialog,
  PackageMetadata,
  ReleaseNotes,
  StepId,
  StepperFormStore,
  Summary,
  SummaryService,
  useFirmwareStore
} from '@ottobock/remote-update/domain';
import { LogService } from '@ottobock/shared/domain';

@Component({
  standalone: true,
  selector: 'remote-update-feature-remote-update-dashboard-create-firmware-package',
  imports: [
    MultiStepForm,
    DeviceCompatibility,
    FileUpload,
    ManifestInformation,
    ReleaseNotes,
    Summary,
    MatProgressBar,
    PackageMetadata,
    Distribution
  ],

  templateUrl: './create-firmware-package.html',
  styleUrls: ['./create-firmware-package.css']
})
export class CreateFirmwarePackage implements OnInit {
  store = inject(StepperFormStore);
  private _snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private dialogRef = inject(MatDialogRef<CreateFirmwarePackage>);
  private firmwareStore = inject(useFirmwareStore);
  private summaryService = inject(SummaryService);
  public allFormData = computed(() => this.store.getAllFormData());
  private router = inject(Router);
  private logService = inject(LogService);

  currentSection = this.store.currentSection();
  currentForm = new FormGroup({});
  firmwareMenuItems = this.store.menuItems;

  isLoading = computed(() => this.firmwareStore.uploadPackage().loading);
  updatePackageModelIsLoading = computed(() => this.firmwareStore.updatePackage().loading);
  isEditingFirmwarePackage = computed(() => this.store.isEditingFirmwarePackage());

  horizontalPosition: MatSnackBarHorizontalPosition = 'start';
  verticalPosition: MatSnackBarVerticalPosition = 'bottom';

  private _files = this.store.selectedFiles;
  get files(): File[] {
    return this._files();
  }

  private firmwarePackageEditData = inject<{
    packageDetails?: FrontendFirmwarePackageDetails;
    packageId: number;
  }>(MAT_DIALOG_DATA);

  constructor() {
    effect(() => {
      this.currentSection = this.store.currentSection();
    });
  }

  ngOnInit() {
    const firmwarePackageDetails = this.firmwarePackageEditData.packageDetails;
    if (firmwarePackageDetails) {
      this.store.editFirmwarePackage(firmwarePackageDetails);
    }
  }

  get canProceed(): boolean {
    return this.store.canNavigateNext();
  }

  get isLastStep(): boolean {
    const order = this.store.stepOrder();
    return this.currentSection === order[order.length - 1];
  }

  get actionButtonLabel(): boolean {
    return this.isLastStep;
  }

  goToNextStep(): void {
    const step = this.currentSection;

    switch (step) {
      case StepId.FileUpload:
        if (this.files.length === 0) {
          this.store.updateFileUpload({ isValid: false }, 'Please upload at least one file');
          return;
        } else {
          this.store.updateFileUpload({ isValid: true }, '');
        }
        break;

      case StepId.ManifestInfo:
        if (!this.store.manifestInfo().isValid) {
          this.store.updateManifestInfo({ isValid: false }, 'Manifest information is required');
          return;
        }
        break;

      case StepId.PackageMetadata:
        if (!this.store.packageMetadata().isValid) {
          this.store.updatePackageMetadata(
            { isValid: false },
            this.store.errors().packageMetadata || 'Package metadata is required'
          );
          return;
        }
        break;

      case StepId.DeviceCompatibility:
        this.store.setShowCompatibilityError(true);
        if (this.store.IsIdenticalOrExceedingVersion()) {
          this.store.setError('deviceCompatibility', '');
          return;
        }

        if (this.store.versionIdMismatch()) {
          this.showVersionMismatchDialog();
          return;
        }

        if (!this.store.deviceCompatibility().isValid) {
          this.store.setError(
            'deviceCompatibility',
            this.store.errors().deviceCompatibility ||
              'Please provide a unique compatible device software version in the correct format'
          );
          return;
        } else {
          this.store.clearError('deviceCompatibility');
        }
        break;

      case StepId.Distribution:
        if (this.store.hasInvalidCompanionAppVersion()) {
          this.store.setError('distribution', 'invalidCompanionAppVersion');
          return;
        }

        if (this.store.isCompanionAppVersionEmpty()) {
          this.store.setError('distribution', 'emptyCompanionAppVersion');
          return;
        }

        if (this.store.isBelowGeneralMinVersion()) {
          this.store.setError('distribution', 'belowGeneralMinVersion');
          return;
        }

        if (!this.store.distribution().isValid) {
          this.store.setError(
            'distribution',
            this.store.errors().distribution || 'invalidDistribution'
          );
          return;
        } else {
          this.store.clearError('distribution');
        }
        break;

      case StepId.ReleaseNotes:
        if (!this.store.releaseNotes().isValid) {
          this.store.setError(
            'releaseNotes',
            this.store.errors().releaseNotes ||
              'Release notes can not be empty. Please provide all release notes'
          );
          return;
        } else {
          this.store.clearError('releaseNotes');
        }
        break;

      case StepId.Summary:
        this.handleSubmit();
        return;
      default:
        return;
    }
    this.store.goToNextStep();
  }

  private async submitCreateForm() {
    try {
      const data = this.allFormData();
      const file = data.fileUpload.file;
      const metadataInput = {
        deviceCompatibility: {
          compatible_device_software_versions:
            data.deviceCompatibility.compatible_device_software_versions ?? [],
          isValid: true
        },
        distribution: {
          access: data.distribution.access,
          supportedCompanionApps: (data.distribution.supportedCompanionApps ?? []).map(app => ({
            name: app.name,
            appleMinimumVersion: app.appleMinimumVersion,
            androidMinimumVersion: app.androidMinimumVersion
          })),
          isValid: true
        },
        packageMetadata: {
          classification: data.packageMetadata.classification,
          packageName: data.packageMetadata.packageName,
          isValid: true
        },
        releaseNotes: {
          release_notes: JSON.stringify({
            notes: data.releaseNotes.release_notes
          }),
          isValid: true
        }
      };
      const metadata = mapToPackageUploadMetadata(metadataInput);
      if (file && metadataInput) {
        await this.summaryService.uploadFirmwareFile(file, metadata);
        this._snackBar.open('Firmware package uploaded successfully!', 'Close');
        this.dialogRef.close('success');
        this.firmwareStore.loadAllPackages();
        this.router.navigate(['/firmware-package']);
        this.store.resetStore();
      }
    } catch (err: unknown) {
      this.logService.error('Error during firmware creating. On submitting the firmware', err);
      const message = 'Something went wrong. Try again.';
      this._snackBar.open(message, 'Close');
    }
  }

  private async submitUpdateForm() {
    const updatePackageContent = this.allFormData();
    const metadataInput = {
      deviceCompatibility: {
        compatible_device_software_versions:
          updatePackageContent.deviceCompatibility.compatible_device_software_versions ?? [],
        isValid: true
      },
      distribution: {
        access: updatePackageContent.distribution.access,
        supportedCompanionApps: (
          updatePackageContent.distribution.supportedCompanionApps ?? []
        ).map(app => ({
          name: app.name,
          appleMinimumVersion: app.appleMinimumVersion,
          androidMinimumVersion: app.androidMinimumVersion
        })),
        isValid: true
      },
      packageMetadata: {
        classification: updatePackageContent.packageMetadata.classification,
        packageName: updatePackageContent.packageMetadata.packageName,
        isValid: true
      },
      releaseNotes: {
        release_notes: JSON.stringify({
          notes: updatePackageContent.releaseNotes.release_notes
        }),
        isValid: true
      }
    };
    const metadata = mapToPackageUploadMetadata(metadataInput);

    try {
      await this.summaryService.updateFirmwarePackage(
        this.firmwarePackageEditData.packageId,
        metadata
      );
      this._snackBar.open('Firmware package updated successfully!', 'Close');
      this.dialogRef.close('success');
      this.firmwareStore.loadAllPackages();
      this.router.navigate(['/firmware-package']);
      this.store.resetStore();
    } catch (error: unknown) {
      this.logService.error('Error while updating the firmware package', error);
      const message = 'Something went wrong. Try again.';
      this._snackBar.open(message, 'Close');
    }
  }

  private async handleSubmit() {
    if (this.isEditingFirmwarePackage()) this.submitUpdateForm();
    else this.submitCreateForm();
  }

  onCancel() {
    const confirmDialog = openConfirmDialog(this.dialog, {
      message: 'Are you sure you want to cancel? You might lose your unsaved changes.',
      leftButton: 'Leave',
      rightButton: 'Stay on page',
      discourageConfirmation: true
    });

    confirmDialog.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.dialogRef.close('cancelled');
        this.store.resetStore();
      }
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification(event: BeforeUnloadEvent) {
    const { packageMetadata, deviceCompatibility, fileUpload, manifestInfo, releaseNotes } =
      this.allFormData();
    const isAnyStepValid = [
      fileUpload,
      manifestInfo,
      packageMetadata,
      deviceCompatibility,
      releaseNotes
    ].some(step => step.isValid);
    if (isAnyStepValid) event.preventDefault();
  }

  private showVersionMismatchDialog() {
    const confirmDialog = openConfirmDialog(this.dialog, {
      title: 'ID Version Mismatch',
      message:
        'The id version of the uploaded firmware differs from one or more compatible versions you entered. Please verify this is intentional before proceeding.',
      rightButton: 'Go back',
      leftButton: 'Proceed anyway',
      height: '220px'
    });

    confirmDialog.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        this.store.clearError('deviceCompatibility');
        this.store.goToNextStep();
      }
    });
  }
}
